/**
 * The Import QuickBooks Configuration section (T4-179) shows only behind the feature flag, and only
 * while QuickBooks is connected. The same condition as shouldShowQboExportDefaults today, kept
 * separate because the two features may diverge.
 */
export const shouldShowQboConfigImport = (hasFlag: boolean, quickbooksEnabled: boolean): boolean =>
	hasFlag && quickbooksEnabled;

/** The reason code an import returns when QuickBooks had nothing to import (spec 032, the import contract). */
export const NOTHING_TO_IMPORT = 'NOTHING_TO_IMPORT';

export type ImportOutcome = {status: 'SUCCESS' | 'FAILED'; reason?: string};
export type ImportResponse = {status: number; json: () => Promise<unknown>};

/**
 * Reads one import response into the option's outcome (spec 032, "What counts as success").
 *
 * Success needs a 200, a non-empty results list and every result SUCCESS. A FAILED run still
 * answers 200, so the HTTP code alone proves nothing; and an empty or missing list is a failure,
 * because [].every() is true and would otherwise report success on a body that said nothing.
 */
export const readImportOutcome = async (response: ImportResponse | null): Promise<ImportOutcome> => {
	// Fresh literals rather than a shared constant, so no caller can mutate a shared outcome.
	if (!response || response.status !== 200) {
		return {status: 'FAILED'};
	}
	let body: unknown;
	try {
		body = await response.json();
	} catch (e) {
		return {status: 'FAILED'};
	}
	const results =
		body && Array.isArray((body as {results?: unknown}).results) ? (body as {results: unknown[]}).results : null;
	if (!results || results.length === 0) {
		return {status: 'FAILED'};
	}
	if (results.every(result => !!result && (result as {status?: unknown}).status === 'SUCCESS')) {
		return {status: 'SUCCESS'};
	}
	const reason = results
		.map(result => (result ? (result as {reason?: unknown}).reason : undefined))
		.find((value): value is string => typeof value === 'string');
	return reason ? {status: 'FAILED', reason} : {status: 'FAILED'};
};
